import React from 'react';
import * as api from 'services/api';
import useRS from 'radioactive-state';
import merge from 'lodash/fp/merge';

import { addMessage } from 'actions/error';
import Message from 'components/Snackbars/Message';

import composeUrl from 'services/dataList/composeUrl';
import dispatchType from 'services/dataList/dispatchType';
import mapDataDefault from 'services/dataList/mapDataDefault';

import store from 'store';
import { bindActionCreators } from 'redux';
import processList from 'services/processList';
import waiter from 'helpers/waitForAction';
import { COLUMNS_LIMIT_DEFAULT, IS_SNAP } from 'variables/main';

const dataMap = {};

const GET_LIST = 'GET_LIST';

const initialState = {
  loading: false,
  error: null,
  count: null,
  page: 1,
  perPage: COLUMNS_LIMIT_DEFAULT,
  data: null,
  filters: {},
  search: '',
  sort: {},
  groupBy: {}
};

let loadTimeout = null;

const useDataList = (
  {
    sourceName,
    dataURL,
    getDataUrl,
    method,
    getQueryBody,
    requestData = {},
    mapData,
    autoLoad,
    searchFilterField = 'name',
    actions,
    staticData = false,
    fetchFuncProp
  },
  options = {}
) => {
  // Store initial filters to preserve them when clearFilters is called
  const initialFilters = options.filters || {};
  const searchDebounceMs = options.searchDebounceMs ?? 300;
  const state = useRS(merge(initialState, options));

  // Monotonic id so an earlier, slower load() can't overwrite a newer one's
  // result (search-as-you-type / filter changes returning out of order).
  const loadSeqRef = React.useRef(0);

  const informMessage = bindActionCreators(addMessage, store.dispatch);

  const fetchFunc = async (params = {}) => {
    const url = (getDataUrl || composeUrl)(dataURL, { ...state, groupBy: state.groupBy, ...params });

    // If URL is null or undefined, return empty data (prevents API calls when professionId is missing)
    if (!url) {
      return [];
    }

    if (staticData && dataMap[url]) {
      return dataMap[url];
    }

    let data;
    let error;

    switch (method) {
      case 'POST': {
        const requestBody = getQueryBody ? getQueryBody(state) : requestData;
        data = await api.post(url, requestBody, dispatchType(sourceName, GET_LIST), store.dispatch);
        break;
      }
      case 'GET':
      default:
        data = await api.get(url, dispatchType(sourceName, GET_LIST), store.dispatch).catch((e) => {
          error = e.message;
        });
        break;
    }

    if (error) {
      informMessage(new Message(error, 'error'));
    }

    if (staticData) {
      dataMap[url] = data;
    }

    if (options.onLoad && typeof options.onLoad === 'function') {
      options.onLoad(data);
    }

    return data;
  };

  const load = async (concat) => {
    // Skip API calls during react-snap prerendering to prevent timeouts
    if (IS_SNAP) {
      state.loading = false;
      state.data = state.data || [];
      return;
    }
    
    const requestId = (loadSeqRef.current += 1);
    try {
      state.loading = true;
      const result = fetchFuncProp ? await fetchFuncProp(state) : await fetchFunc();
      // A newer load() started while this one awaited — drop the stale result.
      if (requestId !== loadSeqRef.current) return;
      Object.assign(state, (mapData || mapDataDefault)(result, state, concat));
      state.loading = false;
    } catch (e) {
      if (requestId !== loadSeqRef.current) return;
      state.error = e.message;
      state.loading = false;
      state.data = [];
    }
  };

  const loadAllDataRequests = (additional = {}) => {
    const perPage = additional.perPage || state.perPage;
    const fetch = fetchFuncProp ? fetchFuncProp : fetchFunc;
    return Array(Math.ceil(state.count / perPage))
      .fill(null)
      .map((_, page) => () => fetch({ ...additional, perPage: state.perPage, page: page + 1 }));
  };

  React.useEffect(() => {
    // Skip API calls during react-snap prerendering to prevent timeouts
    if (IS_SNAP) return;
    
    if (autoLoad && !state.data && !state.error && !state.loading) {
      processList.hasOrSet('useTable' + sourceName, async () => {
        const slowConnection = ['slow-2g', '2g', '3g'].includes(
          navigator?.connection?.effectiveType || '4g'
        );

        const timeout = slowConnection ? 1500 : 100;

        waiter.addAction('useTable' + sourceName, load, timeout);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoLoad, sourceName]);

  const endPointActions = Object.keys(actions || {}).reduce(
    (acc, action) => ({
      ...acc,
      [action]: bindActionCreators(actions[action], store.dispatch)
    }),
    {}
  );

  return {
    ...state,
    search: state.filters[searchFilterField],
    actions: {
      load,
      loadAllDataRequests,
      closeError: () => {
        state.error = null;
      },
      onChangePage: (currentPage, forceLoad = true, concat) => {
        state.page = currentPage + 1;

        clearTimeout(loadTimeout);

        loadTimeout = setTimeout(() => forceLoad && load(concat), 500);
      },
      onChangePerPage: (newPerPage, forceLoad = true) => {
        state.perPage = newPerPage;
        state.page = 1;
        forceLoad && load();
      },
      onSearchChange: (searchText, forceLoad = true, immediate = false) => {
        const oldSearch = state.filters[searchFilterField];
        if (searchText === oldSearch || (!searchText && !oldSearch)) {
          return;
        }
        state.filters = {
          ...state.filters,
          [searchFilterField]: searchText
        };
        state.page = 1;

        clearTimeout(state.timeout);

        // Don't fire on 1-2 chars — wait until the user has typed something
        // searchable. Empty (cleared) and >=3 still fetch. Enter (immediate)
        // bypasses the gate so explicit submits always run.
        const len = (searchText || '').length;
        const tooShort = len > 0 && len < 3;

        if (immediate) {
          forceLoad && load();
        } else if (!tooShort) {
          state.timeout = setTimeout(() => {
            forceLoad && load();
          }, searchDebounceMs);
        }
      },
      onFilterChange: (filters, forceLoad = true) => {
        state.filters = filters;
        state.page = 1;
        store.dispatch({ type: dispatchType(sourceName, 'ON_FILTER_CHANGE'), payload: filters });
        forceLoad && load();
      },
      onSortChange: (column, direction, forceLoad = true) => {
        state.page = 1;
        state.sort = { [column]: direction };
        forceLoad && load();
      },
      clearData: (clearSearch) => {
        if (clearSearch) {
          state.filters = {
            ...state.filters,
            [searchFilterField]: ''
          };
          state.data = null;
          state.page = 1;
          load();
        } else {
          state.data = null;
        }
      },
      clearFilters: () => {
        Object.assign(state, initialState, { filters: initialFilters });
        load();
      },
      ...endPointActions
    }
  };
};

export default useDataList;
